// src/features/tidsregistering/components/attendance/useDragCreateHour.js
import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Manages drag-to-create interactions on an AttendanceRow.
 *
 * Identical in structure to useDragCreate (day view) but works with
 * 15-minute slot indices instead of day indices.
 *
 * Coordinate math:
 *   The row's left edge in screen-space == the first visible slot.
 *   slotIndex = firstVisible + floor(offsetX / slotWidthPx)
 *   time = slotIndexToTime(slotIndex)
 *
 * Props:
 *   visibleSlotIndices — array of visible slot indices (from visibleDayIndices)
 *   slotWidthPx        — px per slot column (from dayWidth)
 *   slotIndexToTime    — (idx) => Date, from useHourViewLogic
 *   onDragCreate       — ({ startTime, endTime }) => void | null
 */
export function useDragCreateHour({ visibleSlotIndices, slotWidthPx, slotIndexToTime, onDragCreate }) {
  const [drag, setDrag] = useState(null); // { startIdx, endIdx } | null
  const rowRef = useRef(null);

  const firstVisible = visibleSlotIndices[0] ?? 0;
  const lastVisible = visibleSlotIndices[visibleSlotIndices.length - 1] ?? firstVisible;

  const xToSlotIndex = useCallback(
    (clientX) => {
      if (!rowRef.current) return firstVisible;
      const rect = rowRef.current.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const raw = firstVisible + Math.floor(offsetX / slotWidthPx);
      return Math.max(firstVisible, Math.min(raw, lastVisible));
    },
    [firstVisible, lastVisible, slotWidthPx],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('[data-time-block="true"]')) return;
      e.preventDefault();
      const idx = xToSlotIndex(e.clientX);
      setDrag({ startIdx: idx, endIdx: idx });
    },
    [xToSlotIndex],
  );

  const handleMouseMove = useCallback(
    (e) => {
      setDrag((prev) => (prev ? { ...prev, endIdx: xToSlotIndex(e.clientX) } : null));
    },
    [xToSlotIndex],
  );

  const handleMouseUp = useCallback(
    (e) => {
      setDrag((prev) => {
        if (!prev) return null;
        const endIdx = xToSlotIndex(e.clientX);
        const startIndex = Math.min(prev.startIdx, endIdx);
        // End time = start of the NEXT slot after the last selected slot
        const endIndex = Math.max(prev.startIdx, endIdx) + 1;
        const startTime = slotIndexToTime(startIndex);
        const endTime = slotIndexToTime(endIndex);
        if (onDragCreate && startTime && endTime) {
          onDragCreate({ startTime, endTime });
        }
        return null;
      });
    },
    [xToSlotIndex, slotIndexToTime, onDragCreate],
  );

  useEffect(() => {
    if (!drag) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [drag, handleMouseMove, handleMouseUp]);

  // Ghost bar pixel dimensions while dragging
  const ghostBar = drag
    ? {
        leftPx: (Math.min(drag.startIdx, drag.endIdx) - firstVisible) * slotWidthPx,
        widthPx: (Math.abs(drag.endIdx - drag.startIdx) + 1) * slotWidthPx - 2,
      }
    : null;

  return { rowRef, isDragging: !!drag, ghostBar, handleMouseDown };
}
